'use client'

import { useCallback, useEffect } from 'react'
import { useIsMutating, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { isSessionStorageKey, onAmplifyRuntimeReady } from '@/lib/amplify'
import { ADMIN_GROUP, endSession, readSession, type Session } from '@/lib/api/session'

export const SESSION_QUERY_KEY = ['session'] as const
const SIGN_OUT_KEY = ['signOut'] as const

/**
 * `unknown` is a real state, not a loading flag. A guard that treats it as `guest` redirects
 * people who are in fact signed in, on every hard refresh.
 */
export type SessionStatus = 'unknown' | 'guest' | 'authenticated'

/** Every cached row but the session itself: whatever was fetched was the previous person's. */
function removeEveryOtherQuery(queryClient: QueryClient): void {
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0] })
}

/**
 * Signed out, as far as this tab is concerned. The session is SET to guest rather than removed, so
 * the observers already on the page (the header, a guard) are told at once; `clear()` would detach
 * them from a query that no longer exists and leave them showing a signed-in state until something
 * else re-rendered them.
 */
function clearSignedInState(queryClient: QueryClient): void {
    queryClient.setQueryData(SESSION_QUERY_KEY, null)
    removeEveryOtherQuery(queryClient)
}

/**
 * Read the session again and, if the person behind it changed — signed out in another tab, or
 * someone else signed in there — drop their rows BEFORE the new session lands, so no query keyed on
 * the new session can start and then be removed from under its observer.
 */
async function recheckSession(queryClient: QueryClient): Promise<void> {
    const before = queryClient.getQueryData<Session | null>(SESSION_QUERY_KEY) ?? null
    const after = await readSession()
    if (before && before.userId !== after?.userId) { removeEveryOtherQuery(queryClient) }
    queryClient.setQueryData(SESSION_QUERY_KEY, after)
}

/**
 * The app's one view of who is signed in — a TanStack query like every other server fact, so
 * the header, the guard and every user-scoped hook read the same cached answer and a sign-out
 * empties all of it at once.
 */
export function useSession() {
    const queryClient = useQueryClient()
    const query = useQuery({
        queryKey: SESSION_QUERY_KEY,
        queryFn: readSession,
        // Nothing marks it stale by time. Sign-in and sign-out replace it, and useSessionEvents
        // re-reads it when Amplify, another tab or a returning tab says it may have changed.
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
        // An unreadable session IS the guest answer (readSession resolves null rather than throwing),
        // never an incident: reporting it would page someone for every signed-out visitor.
        meta: { expectedFailure: true },
    })
    const session: Session | null = query.data ?? null
    const status: SessionStatus = query.isPending ? 'unknown' : session ? 'authenticated' : 'guest'

    /**
     * Re-read the session after a sign-in and report whether one actually resulted. Cognito
     * accepting a code is not the same as the app being signed in — the guard and every query read
     * THIS, so a hydrate that lands on guest is reported rather than bounced silently to /login.
     */
    const hydrate = useCallback(async (): Promise<boolean> => {
        const next = await queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: readSession, staleTime: 0 })
        return next !== null
    }, [queryClient])

    // Sign out AND drop every cached row. In `finally`, because a sign-out that throws (offline, a
    // token already revoked) must still leave this tab showing nobody's data.
    const { mutateAsync } = useMutation({
        mutationKey: SIGN_OUT_KEY,
        mutationFn: async () => {
            try { await endSession() } finally { clearSignedInState(queryClient) }
        },
    })
    // Never rejects: the MutationCache has reported a failure, and the tab is signed out either
    // way, so a caller's `await signOut(); router.push('/')` always gets to navigate.
    const signOut = useCallback(async (): Promise<void> => {
        try { await mutateAsync() } catch { /* reported by the MutationCache */ }
    }, [mutateAsync])
    // Counted across the app, not per caller: the header's and the sidebar's buttons are separate
    // components, and both should say a sign-out is under way, whichever one started it.
    const isSigningOut = useIsMutating({ mutationKey: SIGN_OUT_KEY }) > 0

    return {
        status,
        session,
        userId: session?.userId ?? null,
        email: session?.email ?? null,
        isAuthenticated: status === 'authenticated',
        isAdmin: session?.groups.includes(ADMIN_GROUP) ?? false,
        hydrate,
        signOut,
        isSigningOut,
    }
}

/**
 * Keeps `['session']` true after the moment it was read. Mounted once, by Providers.
 *
 * Read once and never again, the session went on saying "signed in" after a refresh token expired,
 * after a sign-out in another tab and after an admin's group changed — and every user query behind
 * it failed under a header that still showed the account. Three things re-check it:
 *
 * - **Amplify's `auth` events.** `signedIn` and `tokenRefresh` re-read it (a refreshed token is
 *   how a group change arrives); `signedOut` is a sign-out; `tokenRefresh_failure` signs this tab
 *   out without re-reading, because a re-read retries the refresh, fails, and fires the event
 *   again. Attached when the SDK first loads, never by loading it: no event can fire before the SDK
 *   that emits it, and a guest with no stored tokens still never downloads it.
 * - **Another tab's sign-in or sign-out**, which writes the token keys in localStorage and reaches
 *   this tab as a `storage` event.
 * - **The tab coming back into view.** A laptop that slept through a token's life otherwise sits
 *   on stale rows until somebody clicks. For a guest this reads only localStorage.
 */
export function useSessionEvents(): void {
    const queryClient = useQueryClient()

    useEffect(() => {
        let unlisten: (() => void) | null = null
        let cancelled = false
        onAmplifyRuntimeReady(({ Hub }) => {
            if (cancelled) { return }
            unlisten = Hub.listen('auth', ({ payload }) => {
                if (payload.event === 'signedIn' || payload.event === 'tokenRefresh') { void recheckSession(queryClient); return }
                if (payload.event === 'signedOut') { clearSignedInState(queryClient); return }
                if (payload.event === 'tokenRefresh_failure' && queryClient.getQueryData(SESSION_QUERY_KEY)) { clearSignedInState(queryClient) }
            })
        })
        return () => { cancelled = true; unlisten?.() }
    }, [queryClient])

    useEffect(() => {
        // Not before the first read has answered: that read is already the freshest there is.
        const recheck = () => { if (queryClient.getQueryState(SESSION_QUERY_KEY)?.status === 'success') { void recheckSession(queryClient) } }
        const onVisible = () => { if (document.visibilityState === 'visible') { recheck() } }
        const onStorage = (event: StorageEvent) => { if (isSessionStorageKey(event.key)) { recheck() } }
        document.addEventListener('visibilitychange', onVisible)
        window.addEventListener('storage', onStorage)
        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            window.removeEventListener('storage', onStorage)
        }
    }, [queryClient])
}
